import type { ParsedUrlQuery } from 'querystring'
import { useState, useMemo } from 'react'
import { useRouter } from 'next/router'
// @mui
import { Box, Container, Stack, Typography, Divider, Link } from '@mui/material'
import { styled } from '@mui/material/styles'
// hooks
import useSettings from '@/hooks/useSettings'
import useLocales from '@/hooks/useLocales'
import useApp from '@/hooks/useApp'
// layouts
import Layout from '@/layouts/index'
// components
import Page from '@/components/Page'
// sections
import { ShopProductSearch } from '@/sections/e-commerce/shop'
import { settingService, GlobalSetting } from '@/services/page'
import pathService from '@/services/path'
import { PATH_PAGE } from '@/routes/paths'
import { TagsOrder } from '@/generated/graphql'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { APPS } from '@/appConfig'
import sortBy from 'lodash/sortBy'
import cloneDeep from 'lodash/cloneDeep'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { glossaryService, extractTagEntries } from '@/services/glossary'
import { capitalCase } from 'change-case'

interface PathProps extends ParsedUrlQuery {
  appId: string
}

interface TagsPageProps {
  preview: boolean
  totalTags: number
  globalSetting?: GlobalSetting
  allTags: ReturnType<typeof extractTagEntries>
}

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(8),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(11),
  },
}))

// ----------------------------------------------------------------------

Tags.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout variant="main">{page}</Layout>
}

// ----------------------------------------------------------------------
const DEFAULT_TAGS_ORDERS = TagsOrder.SysPublishedAtDesc

export default function Tags({ allTags = [], globalSetting }: TagsPageProps) {
  const { themeStretch } = useSettings()
  const { locale, query } = useRouter()
  const { logo, host, title: rootTitle } = useApp()
  const isLocaleEnglish = useMemo(() => locale === 'en', [locale])

  const [filter, setFilter] = useState<{
    search: string | null
  }>({
    search: null,
  })
  const { translate } = useLocales()

  const querySearch = useMemo<{
    search: string
  }>(
    () => ({
      search: Array.isArray(query.search) ? '' : query.search ?? '',
    }),
    [query]
  )

  const combinedFilter = useMemo<{
    search: string
  }>(
    () => ({
      search: filter.search ?? querySearch.search,
    }),
    [filter, querySearch]
  )

  const filteredTags = useMemo(
    () =>
      sortBy(allTags, (tag) => tag.name.toLocaleLowerCase()).filter((tag) => {
        if (combinedFilter.search) {
          return tag.name.toLowerCase().includes(combinedFilter.search.toLowerCase())
        }
        return true
      }),
    [allTags, combinedFilter]
  )

  const allEnglishLetter = useMemo(() => {
    const letters: string[] = []
    for (let i = 65; i <= 90; i++) {
      letters.push(String.fromCharCode(i))
    }
    return letters
  }, [])

  const OTHERS_LETTER = 'Others'
  const groupByEnglishLetterFilteredTags = useMemo(() => {
    const tagGroups: Record<string, typeof filteredTags> = {}
    let cloneFilteredTags = cloneDeep(filteredTags)
    for (const letter of allEnglishLetter) {
      if (!tagGroups[letter]) {
        tagGroups[letter] = []
      }

      for (const filteredTag of filteredTags) {
        if (filteredTag.name.charAt(0).toUpperCase() === letter) {
          tagGroups[letter].push(filteredTag)
          cloneFilteredTags = cloneFilteredTags.filter((tag) => tag.id !== filteredTag.id)
        }
      }
    }
    tagGroups[OTHERS_LETTER] = cloneFilteredTags
    return tagGroups
  }, [allEnglishLetter, filteredTags])

  const handleScrollToLetter = (letterId: string) => {
    const letterElement = document.getElementById(letterId)
    if (letterElement) {
      const extraHeight = 100
      const top = letterElement.getBoundingClientRect().top
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop

      window.scrollTo({
        top: scrollTop + top - extraHeight,
        behavior: 'smooth',
      })
    }
  }

  return (
    <Page
      title={translate('common.glossary')}
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: translate('common.glossary'),
        url: `https://${host}/${locale}/glossary`,
        '@id': `https://${host}/${locale}/glossary`,
        publisher: {
          '@type': 'Organization',
          name: rootTitle ?? '',
          logo: {
            '@type': 'ImageObject',
            url: logo ?? '',
          },
        },
        mainEntity: {
          '@type': 'ItemList',
          name: translate('common.glossary'),
          itemListElement: filteredTags.map((tag, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: tag.name,
            description: tag?.content?.json
              ? documentToPlainTextString(tag.content.json).slice(250)
              : '',
          })),
        },
      }}
    >
      <RootStyle>
        <Container maxWidth={themeStretch ? false : 'lg'}>
          <Typography textAlign="center" variant="h4" my={5}>
            {translate('common.glossary')}
          </Typography>
          <Stack
            spacing={2}
            direction="column"
            alignItems={{ sm: 'center' }}
            justifyContent="center"
            sx={{ mb: 10, mt: 5 }}
          >
            <ShopProductSearch
              value={combinedFilter.search}
              onSearch={(value) =>
                setFilter((prev) => ({
                  ...prev,
                  search: value,
                }))
              }
            />
            {isLocaleEnglish && (
              <Stack
                spacing={2}
                direction="row"
                alignItems={{ sm: 'center' }}
                justifyContent="center"
                flexWrap="wrap"
                sx={{ mt: 5 }}
              >
                {allEnglishLetter.map((letter) => {
                  const isDisabled = groupByEnglishLetterFilteredTags[letter].length < 1
                  return (
                    <Typography
                      key={letter}
                      variant="h6"
                      sx={{
                        color: isDisabled ? 'text.disabled' : 'text.primary',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => !isDisabled && handleScrollToLetter(letter)}
                    >
                      {letter}
                    </Typography>
                  )
                })}
                {groupByEnglishLetterFilteredTags[OTHERS_LETTER].length > 0 && (
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'text.primary',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleScrollToLetter(OTHERS_LETTER)}
                  >
                    {OTHERS_LETTER}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
          {isLocaleEnglish ? (
            <Box
              sx={{
                display: 'grid',
                gap: 10,
                gridTemplateColumns: {
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                },
              }}
            >
              {Object.entries(groupByEnglishLetterFilteredTags).map(([letter, tags]) =>
                tags.length > 0 ? (
                  <div key={letter + tags.length}>
                    <Typography id={letter} variant="h6" sx={{ color: 'text.primary' }}>
                      {letter}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    {tags.map((tag) => (
                      <Link key={tag.name} href={`${PATH_PAGE.glossary}/${tag.slug}`}>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary', my: 1, cursor: 'pointer' }}
                        >
                          {capitalCase(tag.name)}
                        </Typography>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <></>
                )
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: {
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                },
              }}
            >
              {filteredTags.map((filteredTag) => (
                <Link key={filteredTag.name} href={`${PATH_PAGE.glossary}/${filteredTag.slug}`}>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', my: 1, cursor: 'pointer' }}
                  >
                    {filteredTag.name}
                  </Typography>
                </Link>
              ))}
            </Box>
          )}
        </Container>
      </RootStyle>
    </Page>
  )
}

export const getStaticPaths: GetStaticPaths<PathProps> = async () => {
  const { getDefaultPaths } = pathService()
  const paths = APPS.flatMap((app) => getDefaultPaths(app.id))

  return {
    paths,
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps<TagsPageProps, PathProps> = async ({
  preview = false,
  params,
  locale,
}) => {
  if (!params) throw new Error('No path parameters found')
  const { appId } = params
  const { getAllTagsWithName, getAllTagsTotal } = glossaryService(appId)
  const { getGlobalSetting } = settingService(appId)
  const [globalSetting, allTags, totalTags] = await Promise.all([
    getGlobalSetting({ locale }),
    getAllTagsWithName({ locale, order: DEFAULT_TAGS_ORDERS }),
    getAllTagsTotal({ locale }),
  ])

  return {
    props: {
      preview,
      globalSetting,
      allTags,
      totalTags,
    },
    revalidate: 3600,
  }
}
